import React, { useState, useEffect } from 'react'
import { isMatch, isEmpty } from 'lodash'
import { ChevronDown, ChevronUp } from 'react-feather'
import OutsideClickHandler from 'react-outside-click-handler'
import classNames from 'classnames'
import messages from 'utils/messages'
import Accordion from '../../../../../../components/Accordion/Accordion'
import { Scrollbars } from 'react-custom-scrollbars'
import { FormattedMessage } from 'react-intl'
import { DatePicker } from './DatePicker'
import { SearchIcon, CloseIcon, BxsDownloadIcon } from '../../../../../../images/common'
import { RELATIVE_TIME_RANGE_OPTIONS } from './DatePicker'

const SelectTimeRangeMenu = ({
  options = [],
  selectedOption = {},
  menuOffset = 'left',
  onOptionSelected = (val) => null,
  onClose = (val) => null,
  withDashboardDefault = true
}) => {

  return (
    <OutsideClickHandler onOutsideClick={onClose}>
      <div
        className={classNames('dashboard-time-range-menu', menuOffset && `dashboard-time-range-menu--${menuOffset}`)}
        onClick={event => event.stopPropagation()}
      >
        <DatePicker
          selectedDate={selectedOption}
          relativeTimeOptions={[...options.length > 0 ? options : RELATIVE_TIME_RANGE_OPTIONS]}
          hasDashboardDefaultOption={withDashboardDefault}
          onApplySelectedDate={(timeRange) => {
            console.log({ timeRange })
            if (isEmpty(timeRange)) {
              return onOptionSelected({})
            }
            return onOptionSelected(timeRange)
          }}
        />
      </div>
    </OutsideClickHandler>
  )
}

const SelectSimpleMenu = ({
  options = [],
  onOptionSelected = (val) => null,
  onClose = (val) => null,
  onSearch = (val) => null,
  isSearchable = false,
  hasAccordions = []
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);

  useEffect(() => {
    if (!searchValue) {
      setFilteredOptions(options);
    } else {
      setFilteredOptions(
        options.filter(option =>
          option.label && option.label.toLowerCase().includes(searchValue.toLowerCase())
        )
      )
    }
  }, [searchValue, options])

  const handleSearch = (value) => {
    setSearchValue(value)
    onSearch && onSearch(value)
  }

  return (
    <OutsideClickHandler onOutsideClick={onClose}>
      <div
        className={classNames(
          'chart-editor-select-simple-menu',
          hasAccordions.length > 0 && 'chart-editor-select-simple-menu--accordion',
          isSearchable && 'chart-editor-select-simple-menu--search'
        )}
        onClick={event => event.stopPropagation()}
      >
        {isSearchable &&
          <div className='text-field text-field_nolabel text-field--search'>
            <input
              type="text"
              placeholder="Search"
              className={classNames(
                'text-field__input text-field__input--search',
                hasAccordions.length > 0 && 'viso-border-none'
              )}
              onChange={(e) => handleSearch(e.target.value)}
              value={searchValue}
            />
            <div className="text-field__icon">{!searchValue && <SearchIcon />}</div>
            {searchValue && <span className="cursor text-field__icon text-field__icon_clickable" onClick={() => handleSearch('')}><CloseIcon /></span>}
          </div>
        }
        {hasAccordions.length > 0 ?
          <Accordion
            items={hasAccordions.map(accordion => ({
              title: accordion,
              contentRenderer: () =>
                <div>
                  {filteredOptions.filter(option => option.accordion === accordion).map((option, index) => (
                    <div
                      className='chart-editor-select-simple-menu-icon chart-editor-select-simple-menu-icon--view'
                      key={index}
                      onClick={() => onOptionSelected(option)}
                    >
                      <div className='chart-editor-select-simple-menu-icon__image'>
                        <BxsDownloadIcon />
                      </div>
                      <div className='chart-editor-select-simple-menu-icon__label'>
                        {option.label}
                      </div>
                    </div>
                  ))}
                </div>
            }))}
            fixedHeight={230}
            defaultOpenIndex={0}
            alwaysOpen={true}
          />
          :
          <Scrollbars
            autoHeight
            className="custom-scrollbars"
            autoHeightMax={214}
            universal
            renderThumbVertical={props => <div {...props} className="thumb-vertical" />}
          >
            {(filteredOptions && filteredOptions.length > 0) ?
              (filteredOptions.map((option, index) => (
                <div
                  className='chart-editor-select-simple-menu__option'
                  key={index}
                  onClick={() => onOptionSelected(option)}
                >
                  {option.label}
                </div>
              )))
              :
              (
                <div className='chart-editor-select-simple-menu__option'>
                  <FormattedMessage {...messages.noDataAvailable} />
                </div>
              )
            }
          </Scrollbars>
        }
      </div>
    </OutsideClickHandler>
  )
}

export const Select = ({
  label = '',
  hasBorder = false,
  placeholder = '',
  selectedOption = {},
  isSearchable = false,
  options = [],
  onChange = (val) => null,
  withSimpleMenu = true,
  menuOffset = 'left',
  withTimeRangeMenu = false
}) => {
  const [currentSelectedOption, setCurrentSelectedOption] = useState(selectedOption)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const hasAccordions = [...new Set(options.map(option => option.accordion).filter(Boolean))]

  useEffect(() => {
    if (withTimeRangeMenu) {
      if (
        selectedOption?.start !== currentSelectedOption?.start ||
        selectedOption?.end !== currentSelectedOption?.end
      ) {
        setCurrentSelectedOption(selectedOption)
      }
    } else if (!withTimeRangeMenu) {
      if (!isMatch(selectedOption, currentSelectedOption)) {
        setCurrentSelectedOption(selectedOption)
      }
    }
  }, [selectedOption])

  const handleOnSelection = (newSelectedOption) => {
    setCurrentSelectedOption(newSelectedOption)
    onChange(newSelectedOption)
  }

  const handleToggleMenu = () => {
    setIsMenuOpen(prevState => !prevState)
  }

  const renderInputBoxContent = () => {
    if (currentSelectedOption.label) {
      return (
        <div> {currentSelectedOption.label} </div>
      )
    }
    if (placeholder) {
      return (
        <div className='chart-editor-select-input__box__placeholder'>
          {placeholder}
        </div>
      )
    }
    return null
  }

  return (
    <div className={classNames('chart-editor-select', {
      'chart-editor-border': hasBorder
    })}>
      {label &&
        <div className='chart-editor__label'>
          <label className="text-field__label1">{label}</label>
        </div>
      }
      <div
        className='chart-editor-select-input'
        onClick={handleToggleMenu}
      >
        <div className='chart-editor-select-input__box'>
          {renderInputBoxContent()}
        </div>
        <div className='chart-editor-select-input__icon'>
          {isMenuOpen ? <ChevronUp /> : <ChevronDown />}
        </div>

        {(withSimpleMenu && !withTimeRangeMenu && isMenuOpen) &&
          <SelectSimpleMenu
            isSearchable={isSearchable}
            options={options}
            onOptionSelected={val => {
              handleOnSelection(val)
              handleToggleMenu()
            }}
            onClose={handleToggleMenu}
            hasAccordions={hasAccordions}
          />
        }
        {(withTimeRangeMenu && isMenuOpen) &&
          <SelectTimeRangeMenu
            options={options}
            selectedOption={currentSelectedOption}
            onOptionSelected={val => {
              handleOnSelection(val)
              handleToggleMenu()
            }}
            menuOffset={menuOffset}
            onClose={handleToggleMenu}
          />
        }
      </div>
    </div>
  )
}